import { Alert, Button, Card, DatePicker, Descriptions, Form, Space, App as AntApp } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { lessonKeys } from "../app/lessons.ts";
import type { LessonGenerationResult } from "../app/timetable.ts";

interface Props {
  classId: string;
  yearStart: string;
  yearEnd: string;
}

/** Field names match the API (from, to). */
interface GenerateForm {
  from?: Dayjs;
  to?: Dayjs;
}

/** Initial range: today (or the year start) until the end of that month, inside the academic year. */
function initialRange(yearStart: string, yearEnd: string): GenerateForm {
  const start = dayjs(yearStart);
  const end = dayjs(yearEnd);
  const today = dayjs();
  const from = today.isBefore(start) || today.isAfter(end) ? start : today;
  const monthEnd = from.endOf("month");
  return { from, to: monthEnd.isAfter(end) ? end : monthEnd };
}

/**
 * "Darslarni yaratish": the first click runs a dry run and shows how many lessons would be created; the second creates them.
 * Generation is idempotent on the server, so running it again only adds missing lessons.
 */
export function GenerateLessonsCard({ classId, yearStart, yearEnd }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<GenerateForm>();
  const [preview, setPreview] = useState<LessonGenerationResult>();
  const [summary, setSummary] = useState<string[]>([]);

  const generate = useMutation({
    mutationFn: async ({ values, dryRun }: { values: GenerateForm; dryRun: boolean }) => {
      const body = { from: values.from?.format("YYYY-MM-DD") ?? null, to: values.to?.format("YYYY-MM-DD") ?? null, dryRun };
      const { data, error, response } = await api.POST("/academic/classes/{id}/lessons/generate", { params: { path: { id: classId } }, body });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (result) => {
      setSummary([]);
      if (result.dryRun) {
        setPreview(result);
        return;
      }
      setPreview(undefined);
      void message.success(t("timetable.generated", { count: Number(result.toCreate) }));
      void queryClient.invalidateQueries({ queryKey: lessonKeys.all(classId) });
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const disabledDate = (d: Dayjs) => d.isBefore(dayjs(yearStart), "day") || d.isAfter(dayjs(yearEnd), "day");

  return (
    <Card title={t("timetable.generateTitle")}>
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form<GenerateForm>
        form={form}
        layout="inline"
        requiredMark={false}
        initialValues={initialRange(yearStart, yearEnd)}
        onValuesChange={() => setPreview(undefined)}
        onFinish={(values) => generate.mutate({ values, dryRun: true })}
      >
        <Form.Item name="from" label={t("timetable.from")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <DatePicker format="DD.MM.YYYY" allowClear={false} disabledDate={disabledDate} />
        </Form.Item>
        <Form.Item name="to" label={t("timetable.to")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <DatePicker format="DD.MM.YYYY" allowClear={false} disabledDate={disabledDate} />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" loading={generate.isPending && generate.variables.dryRun}>
            {t("timetable.preview")}
          </Button>
        </Form.Item>
      </Form>
      {preview && (
        <Space orientation="vertical" style={{ marginTop: 16, width: "100%" }}>
          <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label={t("timetable.toCreate")}>{preview.toCreate}</Descriptions.Item>
            <Descriptions.Item label={t("timetable.existing")}>{preview.existing}</Descriptions.Item>
            <Descriptions.Item label={t("timetable.withoutTeacher")}>{preview.withoutTeacher}</Descriptions.Item>
            <Descriptions.Item label={t("timetable.nonSchoolDays")}>{preview.nonSchoolDays}</Descriptions.Item>
          </Descriptions>
          {Number(preview.withoutTeacher) > 0 && <Alert type="warning" showIcon title={t("timetable.withoutTeacherHint")} />}
          <Button
            type="primary"
            disabled={Number(preview.toCreate) === 0}
            loading={generate.isPending && !generate.variables.dryRun}
            onClick={() => generate.mutate({ values: form.getFieldsValue(), dryRun: false })}
          >
            {t("timetable.generate", { count: Number(preview.toCreate) })}
          </Button>
        </Space>
      )}
    </Card>
  );
}
